/**
 * @file TermsOfService.tsx
 * @path apps/website/src/pages/legal/TermsOfService.tsx
 * @description
 */

import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { SeoHelmet as SEOHelmet } from "@/components";
import { NAV_LINKS, siteMetadata } from "@/config";
import { formatPhone } from "@sirbastians/utils";

const TermsOfService = () => {
  return (
    <>
      <SEOHelmet
        pageType="default"
        title="Terms of Service | Sir Bastian's Shuttle Service"
        description="Sir Bastian's Shuttle Service terms of experience. Read our terms and conditions for using our shuttle and tour services."
        url="/terms-of-service"
      />

      <div className="min-h-screen bg-background py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Link
            to="/"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>

          {/* Header */}
          <header className="mb-12">
            <h1 className="text-4xl font-bold mb-4">Terms of Service</h1>
            <p className="text-muted-foreground">
              Last updated: October 3, 2025
            </p>
          </header>

          {/* Content */}
          <div className="prose prose-slate max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-semibold mb-4">
                1. Acceptance of Terms
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                By accessing or using Sir Bastian's Shuttle Service ("the
                Service"), you agree to be bound by these Terms of Service
                ("Terms"). If you do not agree to these Terms, please do not use
                our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">
                2. Service Description
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Sir Bastian's provides:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Student shuttle services to universities and colleges</li>
                <li>Airport transfer services</li>
                <li>Private hire and chauffeur services</li>
                <li>Guided tours of Cape Town and surrounding areas</li>
                <li>Corporate shuttle services</li>
                <li>Wedding and event transportation</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">
                3. Booking and Reservations
              </h2>
              <h3 className="text-xl font-semibold mb-3 mt-6">
                3.1 Booking Process
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Bookings can be made through our website, by phone, or via
                email. All bookings are subject to availability and confirmation
                by Sir Bastian's.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">
                3.2 Payment Terms
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>
                  Full payment is required at the time of booking unless
                  otherwise arranged
                </li>
                <li>
                  We accept various payment methods as indicated during booking
                </li>
                <li>
                  All prices are in South African Rand (ZAR) unless stated
                  otherwise
                </li>
                <li>Prices may be subject to change without notice</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">
                3.3 Cancellation Policy
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>
                  Cancellations made 48+ hours before service: Full refund
                </li>
                <li>
                  Cancellations made 24-48 hours before service: 50% refund
                </li>
                <li>
                  Cancellations made less than 24 hours before service: No
                  refund
                </li>
                <li>No-shows will be charged the full booking amount</li>
                <li>
                  Different cancellation terms may apply to special events and
                  tours
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">
                4. Passenger Responsibilities
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Arrive at designated pick-up points on time</li>
                <li>Provide accurate contact information</li>
                <li>Respect drivers and fellow passengers</li>
                <li>Do not consume alcohol or smoke in vehicles</li>
                <li>Report any damage or issues immediately</li>
                <li>Comply with all safety instructions</li>
                <li>Children under 12 must be accompanied by an adult</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">5. Our Rights</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Sir Bastian's reserves the right to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Refuse service to anyone for any lawful reason</li>
                <li>
                  Modify routes and schedules due to traffic or safety concerns
                </li>
                <li>Cancel services due to severe weather or emergencies</li>
                <li>
                  Remove passengers who behave inappropriately or dangerously
                </li>
                <li>Change prices and service offerings without notice</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">
                6. Liability and Insurance
              </h2>
              <h3 className="text-xl font-semibold mb-3 mt-6">
                6.1 Our Liability
              </h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Sir Bastian's maintains comprehensive insurance for all vehicles
                and operations. We are liable for damages resulting from our
                negligence or breach of contract, subject to the limitations
                below.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">
                6.2 Limitation of Liability
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>
                  We are not liable for delays caused by traffic, weather, or
                  events beyond our control
                </li>
                <li>
                  Our liability is limited to the amount paid for the service
                </li>
                <li>
                  We are not liable for lost or damaged personal belongings
                  unless due to our gross negligence
                </li>
                <li>
                  Passengers travel at their own risk for activities and
                  destinations
                </li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">
                6.3 Passenger Insurance
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                We recommend passengers maintain their own travel insurance for
                medical emergencies, trip cancellations, and personal
                belongings.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">7. Lost Property</h2>
              <p className="text-muted-foreground leading-relaxed">
                While we make reasonable efforts to assist with lost property,
                we cannot guarantee recovery. Items left in vehicles should be
                reported within 24 hours. Unclaimed items may be disposed of
                after 30 days.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">
                8. Intellectual Property
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                All content on our website, including text, images, logos, and
                design, is the property of Sir Bastian's Shuttle Service and
                protected by copyright laws. You may not use our content without
                express written permission.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">9. Privacy</h2>
              <p className="text-muted-foreground leading-relaxed">
                Your use of our services is also governed by our Privacy Policy.
                Please review our{" "}
                <Link
                  to="/privacy-policy"
                  className="text-accent hover:underline"
                >
                  Privacy Policy
                </Link>{" "}
                to understand how we collect and use your information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">
                10. Dispute Resolution
              </h2>
              <h3 className="text-xl font-semibold mb-3 mt-6">
                10.1 Complaints
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                If you have a complaint, please contact us within 7 days of the
                experience. We will investigate and respond within 14 business
                days.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">
                10.2 Governing Law
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                These Terms are governed by the laws of South Africa. Any
                disputes will be resolved in the courts of Cape Town, South
                Africa.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">
                11. Changes to Terms
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                We may modify these Terms at any time. Changes will be posted on
                this page with an updated "Last updated" date. Continued use of
                our services after changes constitutes acceptance of the new
                Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">12. Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                For questions about these Terms, please contact us:
              </p>
              <div className="bg-muted/50 p-6 rounded-lg">
                <p className="text-muted-foreground">
                  <strong>
                    {siteMetadata.title}
                    <br />
                  </strong>
                  <br />
                  Email: {siteMetadata.email}
                  <br />
                  Phone: {formatPhone(siteMetadata.phone)}
                  <br />
                  Address: Cape Town, South Africa
                </p>
              </div>
            </section>
          </div>

          {/* Footer Links */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="flex flex-wrap gap-6 text-sm">
              <Link
                to={NAV_LINKS.experiences.path}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Experiences
              </Link>
              <Link
                to="/privacy-policy"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                to={NAV_LINKS.contactUs.path}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsOfService;
